import { useQuery } from '@hooks/reactQuery';

import { getAntennaModels, getBands4GFdd, getBands4GTdd, getBands5G, getZones } from '@api/manageCell';

import { bandOptionsFormatted, optionsFormattedByTitle } from '@utils/formatter';

import { IBandOption } from '@interfaces/cells/cells';
import { ITitle } from '@interfaces/common/common';
import { TSelectOption } from '@interfaces/components/componentTypes';

import { ETechnologiesValues } from '@enums/common';

import { refetchOptions } from '@constants/common';
import { ANTENNA_MODELS, BANDS_4G_FDD, BANDS_4G_TDD, BANDS_5G, ZONE } from '@constants/queryKeys';

export interface IManageCellOptionsReturnType {
	isLoadingTechnologiesOptions: boolean;
	antennaModelsOptions?: TSelectOption[];
	zoneOptions?: TSelectOption[];
	band4GFddOptions: IBandOption[];
	band4GTddOptions: IBandOption[];
	band5GOptions: IBandOption[];
}

export const useManageCellOptions = (): IManageCellOptionsReturnType => {
	const { data: antennaModelsOptions } = useQuery({
		queryKey: [ANTENNA_MODELS],
		queryFn: () => getAntennaModels(),
		select: (antennaModels: Array<ITitle>) => optionsFormattedByTitle(antennaModels),
		...refetchOptions,
	});

	const { data: zoneOptions } = useQuery({
		queryKey: [ZONE],
		queryFn: () => getZones(),
		select: (zones: Array<ITitle>) => optionsFormattedByTitle(zones),
		refetchOnWindowFocus: false,
	});

	const {
		data: band4GFddOptions,
		isLoading: isLoadingBand4GFddOptions,
	} = useQuery({
		queryKey: [BANDS_4G_FDD],
		queryFn: () => getBands4GFdd(),
		select: (bands4GFdd: Array<IBandOption>) => {
			return bandOptionsFormatted<IBandOption>(bands4GFdd, ETechnologiesValues.T_4G_FDD);
		},
		refetchOnWindowFocus: false,
	});

	const {
		data: band4GTddOptions,
		isLoading: isLoadingBand4GTddOptions,
	} = useQuery({
		queryKey: [BANDS_4G_TDD],
		queryFn: () => getBands4GTdd(),
		select: (bands4GTdd: Array<IBandOption>) => {
			return bandOptionsFormatted<IBandOption>(bands4GTdd, ETechnologiesValues.T_4G_TDD);
		},
		refetchOnWindowFocus: false,
	});

	const {
		data: band5GOptions,
		isLoading: isLoadingBand5GOptions,
	} = useQuery({
		queryKey: [BANDS_5G],
		queryFn: () => getBands5G(),
		select: (bands5G: Array<IBandOption>) => bandOptionsFormatted<IBandOption>(bands5G, ETechnologiesValues.T_5G),
		refetchOnWindowFocus: false,
	});

	return {
		isLoadingTechnologiesOptions: isLoadingBand4GFddOptions || isLoadingBand4GTddOptions || isLoadingBand5GOptions,
		antennaModelsOptions,
		zoneOptions,
		band4GFddOptions: band4GFddOptions || [],
		band4GTddOptions: band4GTddOptions || [],
		band5GOptions: band5GOptions || [],
	};
};
